import { QueryKey } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { INoteViewModel } from "../models/noteModel";
import {
  NoteFormData,
  noteFormDefaultValues,
  noteFormSchema,
} from "../models/noteFormData";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApiMutation } from "../../../../infrastructure/hooks/useApiMutation";
import { useEffect, useState } from "react";

interface UseNoteFormProps {
  note?: INoteViewModel | null;
  entityId?: number;
  createEndpoint: string;
  updateEndpoint: string;
  invalidateKeys: QueryKey[];
  onSuccess?: () => void;
}

export const useNoteForm = ({
  note,
  entityId,
  createEndpoint,
  updateEndpoint,
  invalidateKeys,
  onSuccess,
}: UseNoteFormProps) => {
  const isEditing = !!note;
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<NoteFormData>({
    resolver: yupResolver(noteFormSchema),
    defaultValues: noteFormDefaultValues,
  });

  useEffect(() => {
    // if editing
    if (!!note) {
      form.setValue("entityId", note.id);
      form.setValue("content", note.content);
      return;
    }

    // if creating brand new note
    if (entityId) {
      form.setValue("entityId", entityId);
    }
  }, [note, entityId]);

  const { mutateAsync: createMutation } = useApiMutation();
  const { mutateAsync: updateMutation } = useApiMutation();

  const onSubmit = async (raw: NoteFormData) => {
    const data = { ...raw, content: raw.content.trim() };
    if (isEditing) {
      try {
        setIsLoading(true);
        await updateMutation(
          {
            endpoint: updateEndpoint,
            method: "put",
            invalidateKeys,
            ...data,
          },
          {
            onSuccess: () => {
              form.reset();
              onSuccess?.();
            },
            onSettled: () => {
              setIsLoading(false);
            },
          }
        );
      } catch {
        // already handled
      }
    } else {
      // Create new note
      try {
        setIsLoading(true);
        await createMutation(
          {
            endpoint: createEndpoint,
            method: "post",
            invalidateKeys,
            ...data,
          },
          {
            onSuccess: () => {
              form.reset();
              onSuccess?.();
            },
            onSettled: () => {
              setIsLoading(false);
            },
          }
        );
      } catch {
        // already handled
      }
    }
  };

  const handleSubmit = form.handleSubmit(onSubmit);

  return {
    form,
    handleSubmit,
    isEditing,
    isLoading,
  };
};
